import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import { nextPastedName, shapeDisplayLabel } from '../shapes/shapeLabels'

type Params = {
  canEdit: boolean
  selectedIds: string[]
  shapes: VisualShape[]
  width: number
  height: number
  onChange: (shapes: VisualShape[]) => void
  onSelectIds: (ids: string[]) => void
  onRemove: (ids: string[]) => void
}

export const useShapeKeyboard = ({
  canEdit,
  selectedIds,
  shapes,
  width,
  height,
  onChange,
  onSelectIds,
  onRemove,
}: Params) => {
  const [clipboard, setClipboard] = useState<VisualShape[]>([])

  const latestRef = useRef({
    selectedIds,
    shapes,
    width,
    height,
    onChange,
    onSelectIds,
    onRemove,
    clipboard,
  })
  latestRef.current = {
    selectedIds,
    shapes,
    width,
    height,
    onChange,
    onSelectIds,
    onRemove,
    clipboard,
  }

  useEffect(() => {
    if (!canEdit) return
    const handleKeyDown = (event: KeyboardEvent) => {
      const {
        selectedIds,
        shapes,
        onChange,
        onSelectIds,
        onRemove,
        clipboard,
      } = latestRef.current
      const isMeta = event.metaKey || event.ctrlKey
      const isInputFocused =
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      if (
        isMeta &&
        event.key === 'c' &&
        selectedIds.length > 0 &&
        !isInputFocused
      ) {
        setClipboard(shapes.filter((s) => selectedIds.includes(s.id)))
      }
      if (
        isMeta &&
        event.key === 'v' &&
        clipboard.length > 0 &&
        !isInputFocused
      ) {
        const taken = new Set(shapes.map((s) => s.name))
        const pasted = clipboard.map((shape) => {
          const baseName = shape.name ?? shapeDisplayLabel(shape)
          const name = nextPastedName(baseName, taken)
          taken.add(name)
          return {
            ...shape,
            id: uuid(),
            name,
            x: Math.min(shape.x + 0.03, 1),
            y: Math.min(shape.y + 0.03, 1),
          }
        })
        onChange([...shapes, ...pasted])
        onSelectIds(pasted.map((s) => s.id))
      }
      if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        selectedIds.length > 0 &&
        !(event.target instanceof HTMLInputElement) &&
        !(event.target instanceof HTMLTextAreaElement)
      ) {
        onRemove(selectedIds)
      }
      if (event.key === 'Escape' && selectedIds.length > 0) {
        onSelectIds([])
      }
      const arrowDelta: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      }
      if (arrowDelta[event.key] && selectedIds.length > 0 && !isInputFocused) {
        event.preventDefault()
        const { width, height } = latestRef.current
        const [dx, dy] = arrowDelta[event.key]
        const stepX = width > 0 ? 1 / width : 0
        const stepY = height > 0 ? 1 / height : 0
        onChange(
          shapes.map((s) =>
            selectedIds.includes(s.id) && !s.locked
              ? {
                  ...s,
                  x: Math.max(0, Math.min(1, s.x + dx * stepX)),
                  y: Math.max(0, Math.min(1, s.y + dy * stepY)),
                }
              : s,
          ),
        )
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canEdit])
}
